import {
  atom,
  selector,
  useRecoilRefresher_UNSTABLE,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState
} from 'recoil'

import { KEYCLOAK_CLIENT_ID } from '../../configs/api'
import { getUserInfo } from '../../services/AuthService'
import { roleDetail, roleMapping } from '../../services/KeycloakService'
import {
  checkIsTrue,
  getAttributes,
  isLoggedIn,
  sleep
} from '../../utilities/helpers'

const localStorageEffect =
  (key: string) =>
  // @ts-ignore
  ({ setSelf, onSet }) => {
    const savedValue = localStorage.getItem(key)
    if (savedValue != null) {
      setSelf(JSON.parse(savedValue))
    }

    onSet((newValue: any, _: any, isReset: any) => {
      isReset
        ? localStorage.removeItem(key)
        : localStorage.setItem(key, JSON.stringify(newValue))
    })
  }

const currentUserIdState = atom({
  key: 'CurrentUserId',
  default: '',
  effects: [localStorageEffect('current_user_id')]
})

const fetchAssignedRolePermissionEffect = async (userId: string) => {
  if (!userId) return []
  if (!isLoggedIn()) return []

  await sleep(500)
  const response = await roleMapping(userId)
  const { clientMappings } = response
  if (!clientMappings) return []

  const { mappings } = clientMappings[KEYCLOAK_CLIENT_ID]

  let _permissionKeys: any[] = []
  const results = await Promise.all(
    mappings.map(async (role: { id: string }) => {
      const res = await roleDetail(role.id)
      const {
        attributes: { permissions }
      } = res
      return permissions ?? []
    })
  )
  _permissionKeys = [..._permissionKeys, ...results.flat()]
  return _permissionKeys
}

const fetchUserInfoEffect = async (userId: string) => {
  if (!userId || !isLoggedIn()) return {}

  await sleep(500)
  return await getUserInfo(userId)
}

const permissionListQuery = selector({
  key: 'PermissionList',
  get: async ({ get }) => {
    return await fetchAssignedRolePermissionEffect(get(currentUserIdState))
  },
  cachePolicy_UNSTABLE: {
    // Only store the most recent set of dependencies and their values
    eviction: 'most-recent'
  }
})

const userInfoQuery = selector({
  key: 'UserInfo',
  get: async ({ get }) => {
    return await fetchUserInfoEffect(get(currentUserIdState))
  },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent'
  }
})

export const useRolePermissions = () => {
  const resetUserId = useResetRecoilState(currentUserIdState)
  const setUserIdState = useSetRecoilState(currentUserIdState)
  const permissionList = useRecoilValue(permissionListQuery)
  const currentUserId = useRecoilValue(currentUserIdState)

  const hasPermissions = (permission: any): boolean => {
    if (!permission || permission === '') return true
    return permissionList.includes(permission)
  }

  return {
    currentUserId,
    permissionList,
    hasPermissions,
    setUserIdState,
    resetUserId
  }
}

export const useUserInfo = () => {
  const userInfo = useRecoilValue(userInfoQuery)
  const refreshUserInfo = useRecoilRefresher_UNSTABLE(userInfoQuery)

  const isOwner = (): boolean => {
    const { attributes } = userInfo
    let is_owner = getAttributes(attributes, 'isOwner')
    return checkIsTrue(is_owner)
  }

  return {
    userInfo,
    isOwner,
    refreshUserInfo
  }
}
